import { useLocation } from 'react-router-dom'
import { UseSummaryCartDocument, UseSummaryCartQuery, UseSummaryOrderDocument, UseSummaryOrderQuery } from '../graphql'

import getPhoenixUrlParams from '../lib/get-phoenix-url-params'
import Price from '../typings/Price'
import { PriceTableItem } from '../typings/PriceTable'

import { isConfirmWithOption, SettleWith } from './../typings/Product'

import Routes from './routes'
import useCart from './useCart'
import useOrder from './useOrder'

interface Result {
  isLoading?: boolean
  items?: Array<PriceTableItem>
  total: Price | null
  showVoucher: boolean
  isSettleWithAConfirmWithOption: boolean
}

const DEFAULT_TOTAL_PRICE: Price = { value: 0, currency: 'EUR' }
const DEFAULT_SHOW_VOUCHER = false
const DEFAULT_IS_SETTLE_WITH_A_CONFIRM_WITH_OPTION = false

const useSummary = (): Result => {
  const location = useLocation()
  const { cartId, orderId } = getPhoenixUrlParams(location.search)

  const { cartData, isCartLoading } = useCart<UseSummaryCartQuery>(UseSummaryCartDocument, {
    skip: !cartId,
  })

  const { orderData, isOrderLoading } = useOrder<UseSummaryOrderQuery>(UseSummaryOrderDocument, {
    skip: !orderId,
  })

  const summaryData = (cartData && cartData.cart) || (orderData && orderData.order)
  const isSummaryLoading = isCartLoading || isOrderLoading
  const isVoucherEnabled = cartData && cartData.cart.isVoucherEnabled
  const isSettleWithAConfirmWithOption = Boolean(
    cartData &&
      cartData.cart.items[0].settleWith &&
      isConfirmWithOption(cartData.cart.items[0].settleWith as SettleWith),
  )
  const showVoucher = Boolean(
    isVoucherEnabled && !isSettleWithAConfirmWithOption && location.pathname !== Routes.PAYMENT,
  )

  if (!summaryData || (summaryData && summaryData.priceTable === null)) {
    return {
      total: DEFAULT_TOTAL_PRICE,
      showVoucher: DEFAULT_SHOW_VOUCHER,
      isSettleWithAConfirmWithOption: DEFAULT_IS_SETTLE_WITH_A_CONFIRM_WITH_OPTION,
    }
  }

  const addOnPriceTableItems =
    summaryData.priceTable?.itemsV3.filter((item) => item.__typename === 'PriceTableItemAddon') || []
  const nonAddOnPriceTableItems =
    summaryData.priceTable?.itemsV3.filter((item) => item.__typename !== 'PriceTableItemAddon') || []

  const orderedPriceTableItems = [...nonAddOnPriceTableItems, ...addOnPriceTableItems]

  return {
    isLoading: isSummaryLoading,
    total: summaryData.priceTable!.total,
    items: orderedPriceTableItems,
    showVoucher,
    isSettleWithAConfirmWithOption,
  }
}

export default useSummary
